<template>
  <div class="main">
    <!-- Warning part -->
    <div v-if="!surveyStarted" class="survey-intro">
      <h2 class="intro-title">{{ $t("surveyIntroTitle") }}</h2>
      <p class="intro-text">{{ $t("surveyIntroText") }}</p>
      <button class="content-button primary" @click="startSurvey">
        {{ $t("startSurvey") }}
      </button>
    </div>

    <div v-else-if="currentQuestionIndex < questions.length" class="survey-questions">
      <div class="delete-all-answers-button" v-on:click="openModal">{{ $t('deleteAllAnswers') }}</div>

      <div v-if="isModalVisible" class="modal-overlay" v-on:click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <button v-on:click="closeModal" class="close-btn">&times;</button>
          </div>
          <div class="modal-body">
            <p>{{ $t("reserSurveyQuestion") }}</p>
            <div class="survey-btn-container">
              <button v-on:click="resetSurvey()" class="reset-survey-button">
                {{ $t("confirm") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <h2 class="question-title">{{ questions[currentQuestionIndex].text }}</h2>

      <!-- Rating part -->
      <div v-if="questions[currentQuestionIndex].type === 'rating'" class="survey-options">
        <p class="options-explanation">{{ getRatingExplanation }}</p>
        <label v-for="option in 5" :key="option" class="option-label">
          <input type="radio" :value="option" v-model="getAnswerForCurrentQuestion" class="radio-input" />
          <span class="radio-custom"></span>
          <span class="option-text">{{ option }}</span>
        </label>
      </div>

      <!-- Multiple select part -->
      <div v-else-if="questions[currentQuestionIndex].type === 'multiple'" class="survey-options">
        <p class="options-explanation">{{ $t("multipleExplanation") }}</p>
        <label v-for="(option, index) in questions[currentQuestionIndex].options" :key="index" class="option-label">
          <input type="checkbox" :value="option" v-model="answer2" class="checkbox-input" />
          <span class="checkbox-custom"></span>
          <span class="option-text">{{ option }}</span>
        </label>
      </div>

      <!-- Text question -->
      <div v-else-if="questions[currentQuestionIndex].type === 'text'" class="text-area-wrapper">
        <textarea v-model="getAnswerForCurrentQuestion" :placeholder="$t('leaveAComment')"
          class="text-question"></textarea>
      </div>

      <div class="navigation-buttons">
        <button v-if="currentQuestionIndex > 0" v-on:click="previousQuestion" class="content-button">
          {{ $t("previousStep") }}
        </button>

        <button v-on:click="nextQuestion" class="content-button primary">
          {{ isLastQuestion ? $t("finish") : $t("nextQuestion") }}
        </button>
      </div>
    </div>

    <div v-else-if="!isSubmitted" class="survey-submit">
      <h3 class="completion-title">{{ $t("surveyCompleted") }}</h3>
      <button class="content-button primary-submit" style="display: flex;" v-on:click="submitSurveyData()"
        :disabled="isSubmitting">
        {{ isSubmitting ? $t("submitting") : $t("submit") }}
      </button>
    </div>

    <div v-else class="submission-result">
      <div v-if="submissionSuccess" class="success-message">
        <h3 class="success-title">{{ $t("thankYou") }}</h3>
        <p class="success-text">{{ $t("surveySubmitted") }}</p>
      </div>

      <div v-else class="error-message">
        <h3 class="error-title">{{ $t("error") }}</h3>
        <p class="error-text">{{ $t("submissionError") }}</p>
        <button class="content-button primary" @click="resetSubmission">
          {{ $t("tryAgain") }}
        </button>
      </div>
    </div>

    <div v-if="surveyStarted && !isSubmitted" class="progress">
      <div class="progress-bar" :style="{ width: progress + '%' }" :class="{ 'progress-complete': progress === 100 }">
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import { mapActions } from "vuex/dist/vuex.common.js";

export default {
  data() {
    return {
      surveyStarted: false,
      currentQuestionIndex: 0,
      isModalVisible: false,
      answer1: null,
      answer2: [], // Array for multiple selection for second question
      answer3: null,
      answer4: null,
      answer5: null,
      answer6: null,
      answer7: null,
      answer8: null,
      answer9: null,
      answer10: null,
      answer11: null,
      isSubmitted: false,
      isSubmitting: false,
      submissionSuccess: false,
      errorMessage: '',
      questions: [
        { text: this.$t("surveyQuestion1"), type: 'rating' },
        { text: this.$t("surveyQuestion2"), type: 'multiple', options: [this.$t("q2Option1"), this.$t("q2Option2"), this.$t("q2Option3"), this.$t("q2Option4"), this.$t("q2Option5"), this.$t("q2Option6"), this.$t("q2Option7")] },
        { text: this.$t("surveyQuestion3"), type: 'rating' },
        { text: this.$t("surveyQuestion4"), type: 'rating' },
        { text: this.$t("surveyQuestion5"), type: 'rating' },
        { text: this.$t("surveyQuestion6"), type: 'rating' },
        { text: this.$t("surveyQuestion7"), type: 'rating' },
        { text: this.$t("surveyQuestion8"), type: 'rating' },
        { text: this.$t("surveyQuestion9"), type: 'rating' },
        { text: this.$t("surveyQuestion10"), type: 'text', placeholder: this.$t("textPlaceholder") },
        { text: this.$t("surveyQuestion11"), type: 'text', placeholder: this.$t("textPlaceholder") }
      ]
    };
  },
  computed: {
    ...mapGetters(["getSelectedLanguage"]),
    getAnswerForCurrentQuestion: {
      get() {
        switch (this.currentQuestionIndex) {
          //case 1 - question 2 is loaded differenty because its list
          case 0: return this.answer1;
          case 2: return this.answer3;
          case 3: return this.answer4;
          case 4: return this.answer5;
          case 5: return this.answer6;
          case 6: return this.answer7;
          case 7: return this.answer8;
          case 8: return this.answer9;
          case 9: return this.answer10;
          case 10: return this.answer11;
          default: return null;
        }
      },
      set(value) {
        switch (this.currentQuestionIndex) {
          case 0: this.answer1 = value; break;
          case 2: this.answer3 = value; break;
          case 3: this.answer4 = value; break;
          case 4: this.answer5 = value; break;
          case 5: this.answer6 = value; break;
          case 6: this.answer7 = value; break;
          case 7: this.answer8 = value; break;
          case 8: this.answer9 = value; break;
          case 9: this.answer10 = value; break;
          case 10: this.answer11 = value; break;
          default: break;
        }
      }
    },
    getRatingExplanation() {
      switch (this.currentQuestionIndex) {
        case 0:
          return this.$t("ratingExplanation1");
        case 2:
          return this.$t("ratingExplanation2");
        case 7:
          return this.$t("ratingExplanation3");
        default:
          return this.$t("ratingExplanation1");
      }
    },
    progress() {
      return ((this.currentQuestionIndex + 1) / this.questions.length) * 100;
    },
    isLastQuestion() {
      return this.currentQuestionIndex === this.questions.length - 1;
    }
  },
  methods: {
    ...mapActions(["submitSurvey"]),
    startSurvey() {
      this.surveyStarted = true;
      this.isModalVisible = false;
    },
    nextQuestion() {
      if (this.currentQuestionIndex === 1 && this.answer2.length === 0) {
      }
      this.currentQuestionIndex++;
    },
    previousQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },
    resetSubmission() {
      this.isSubmitted = false;
      this.isSubmitting = false;
      this.submissionSuccess = false;
      this.errorMessage = '';
    },
    resetSurvey() {
      try {
        this.answer1 = null;
        this.answer2 = [];
        this.answer3 = null;
        this.answer4 = null;
        this.answer5 = null;
        this.answer6 = null;
        this.answer7 = null;
        this.answer8 = null;
        this.answer9 = null;
        this.answer10 = null;
        this.answer11 = null;
        this.currentQuestionIndex = 0;
        this.surveyStarted = false;
        isModalVisible = false;
      }
      catch (error) {
        // console.error('Survey reset error:', error);
      }


    },
    openModal() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.comment = '';
    },
    async submitSurveyData() {
      this.isSubmitted = false;
      this.isSubmitting = true;
      this.submissionSuccess = false;
      this.errorMessage = '';

      const surveyData = {
        answer1: this.answer1,
        answer2: this.answer2,
        answer3: this.answer3,
        answer4: this.answer4,
        answer5: this.answer5,
        answer6: this.answer6,
        answer7: this.answer7,
        answer8: this.answer8,
        answer9: this.answer9,
        answer10: this.answer10,
        answer11: this.answer11
      };

      try {
        const response = await this.submitSurvey(surveyData);

        if (response && response.status === 200) {
          this.isSubmitted = true;
          this.isSubmitting = false;
          this.submissionSuccess = true;
        } else {
          throw new Error(this.$t("unknownError"));
        }
      } catch (error) {
        // console.error('Survey submission error:', error);
        this.isSubmitted = true;
        this.isSubmitting = false;
        this.submissionSuccess = false;
        this.errorMessage = error.message || this.$t("submissionError");
      }
    },
    loadQuestions(){
      this.questions= [
        { text: this.$t("surveyQuestion1"), type: 'rating' },
        { text: this.$t("surveyQuestion2"), type: 'multiple', options: [this.$t("q2Option1"), this.$t("q2Option2"), this.$t("q2Option3"), this.$t("q2Option4"), this.$t("q2Option5"), this.$t("q2Option6"), this.$t("q2Option7")] },
        { text: this.$t("surveyQuestion3"), type: 'rating' },
        { text: this.$t("surveyQuestion4"), type: 'rating' },
        { text: this.$t("surveyQuestion5"), type: 'rating' },
        { text: this.$t("surveyQuestion6"), type: 'rating' },
        { text: this.$t("surveyQuestion7"), type: 'rating' },
        { text: this.$t("surveyQuestion8"), type: 'rating' },
        { text: this.$t("surveyQuestion9"), type: 'rating' },
        { text: this.$t("surveyQuestion10"), type: 'text', placeholder: this.$t("textPlaceholder") },
        { text: this.$t("surveyQuestion11"), type: 'text', placeholder: this.$t("textPlaceholder") }
      ]
    }
  },
  watch: {
    getSelectedLanguage(){
      this.loadQuestions();
    }
  }
};
</script>

<style scoped>
.main {
  padding: 3rem 8%;
  max-width: 800px;
  margin: 2% auto;
  background: var(--light-color);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.survey-intro {
  text-align: center;
  animation: fadeIn 0.5s ease-in-out;
}

.intro-title {
  color: var(--main-color);
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.intro-text {
  color: #333;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.question-title {
  color: var(--main-color);
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.options-explanation {
  margin-bottom: 1rem;
  color: #555;
  font-size: 1rem;
  line-height: 1.4;
}

.survey-questions {
  animation: fadeIn 0.5s ease-in-out;
  max-width: 95%;
}

.survey-options {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}

.option-label {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e0e4f1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.option-label:hover {
  border-color: var(--main-color);
  background: rgba(36, 59, 128, 0.05);
}

.radio-input {
  display: none;
}

.checkbox-input {
  display: none;
}

.radio-custom {
  width: 20px;
  height: 20px;
  border: 2px solid var(--main-color);
  border-radius: 50%;
  margin-right: 12px;
  position: relative;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid var(--main-color);
  border-radius: 4px;
  margin-right: 12px;
  position: relative;
}

.radio-custom::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  width: 10px;
  height: 10px;
  background: var(--main-color);
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.checkbox-custom::after {
  content: '';
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0) rotate(45deg);
  width: 6px;
  height: 10px;
  border-right: 2px solid white;
  border-bottom: 2px solid white;
  transition: transform 0.2s ease;
}

.radio-input:checked+.radio-custom::after {
  transform: translate(-50%, -50%) scale(1);
}

.checkbox-input:checked+.checkbox-custom {
  background-color: var(--main-color);
}

.checkbox-input:checked+.checkbox-custom::after {
  transform: translate(-50%, -50%) scale(1) rotate(45deg);
}

.option-text {
  color: var(--main-color);
  font-size: 1rem;
}

.text-area-wrapper {
  margin-bottom: 2rem;

}

.text-question {
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e0e4f1;
  border-radius: 8px;
  font-size: 18px;
  transition: border-color 0.3s ease;
  resize: vertical;
  background-color: var(--light-color);
}

.text-question:focus {
  outline: none;
  border-color: var(--main-color);
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
}

.content-button {
  padding: 0.8rem 1.8rem;
  color: var(--main-color);
  background-color: transparent;
  border: 2px solid var(--main-color);
  border-radius: 8px;
  font-weight: 600;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: center;
}

.content-button.primary {
  background-color: var(--main-color);
  color: white;
  margin-left: auto;
  /* margin-right: auto; */
  text-align: center;
}

.content-button.primary-submit {
  background-color: var(--main-color);
  color: white;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.content-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(36, 59, 128, 0.2);
}

.content-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.progress {
  margin-top: 2rem;
  height: 8px;
  background-color: #e0e4f1;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--main-color);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.progress-complete {
  animation: pulse 2s infinite;
}

.completion-title {
  color: var(--main-color);
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-align: center;
}


.submission-result {
  text-align: center;
  padding: 2rem;
  animation: fadeIn 0.5s ease-in-out;
}

.success-message {
  color: green;
}

.error-message {
  color: red;
}

.delete-all-answers-button {
  text-align: end;
  color: var(--main-color);
  cursor: pointer;
}


.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: hidden;
}

.modal-content {
  background-color: var(--light-color);
  padding: 20px;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 10000;
  color: var(--main-color)
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px;
}

.close-btn {
  font-size: 24px;
  color: #333;
  background: none;
  border: none;
  cursor: pointer;
  margin-left: auto;
}

.close-btn:hover {
  color: #d9534f;
}

.survey-btn-container {
  text-align: right;
}

.reset-survey-button {
  padding: 0.8rem 1.8rem;
  color: var(--main-color);
  background-color: transparent;
  border: 2px solid var(--main-color);
  border-radius: 8px;
  font-weight: 600;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;

}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }

  100% {
    opacity: 1;
  }
}

@media only screen and (max-width: 992px) {
  .main {
    padding: 2rem 5%;
    margin: 2% 5%;
  }
}

@media (max-width: 768px) {

  .survey-options {
    grid-template-columns: 1fr;
  }

  .navigation-buttons {
    flex-direction: column;
  }

  .content-button {
    width: 100%;
  }

  .content-button.primary {
    display: unset !important;
  }

}
</style>